import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Clock, Shield, CheckCircle, Phone, Mail, MapPin, FileText, UserCheck, Bell } from 'lucide-react';
import DVSADisclaimer from '../components/DVSADisclaimer';

const Home = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    testType: '',
    licenseNumber: '',
    preferredDate: '',
    testCentre: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - in real app this would go to backend
    console.log('Form submitted:', formData);
    toast.success('Booking request submitted! We will contact you within 24 hours.');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      testType: '',
      licenseNumber: '',
      preferredDate: '',
      testCentre: '',
      message: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20 px-4">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-full">
              🇬🇧 Official UK Driving Test Booking
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Book Your Driving Test in Minutes
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Fast, secure, and reliable booking service for all UK test centres. No waiting lists.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Book Now
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-6 text-lg">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">How Our Service Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, fast, and reliable booking assistance in just 3 easy steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 h-full border-2 border-blue-200 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div className="text-center">
                  <div className="inline-block bg-blue-900 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                    Step 1
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">Submit Your Request</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Fill out our simple booking form with your details, preferred test date, and test centre location. Takes just 2 minutes.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-8 h-full border-2 border-red-200 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <UserCheck className="w-8 h-8 text-white" />
                </div>
                <div className="text-center">
                  <div className="inline-block bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                    Step 2
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">We Process & Confirm</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our team reviews your request and contacts you within 24 hours to confirm availability and finalize your booking details.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 h-full border-2 border-blue-200 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Bell className="w-8 h-8 text-white" />
                </div>
                <div className="text-center">
                  <div className="inline-block bg-blue-900 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                    Step 3
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">Get Confirmation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Receive your test booking confirmation via email with all the details. We'll send you reminders before your test date.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-gray-50 rounded-xl p-8 border-2 border-gray-200">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-bold mb-4 text-gray-900">What's Included in Our Service?</h3>
              <ul className="text-left space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                  <span>Personal assistance with booking process and form completion</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                  <span>Availability checks across all UK test centres</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                  <span>Email confirmation and test reminders</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                  <span>Customer support throughout the booking process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                  <span>Assistance with rescheduling if needed</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Book Up to 24 Weeks</h3>
              <p className="text-gray-600">Plan ahead with flexible booking slots up to 24 weeks in advance.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">2-Minute Booking</h3>
              <p className="text-gray-600">Quick and easy online booking process. No phone queues.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Secure & Trusted</h3>
              <p className="text-gray-600">TESTBOOK LTD - Registered company with secure payment processing.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">All Test Types</h3>
              <p className="text-gray-600">Car, Motorcycle, Lorry, Bus, and ADI practical tests available.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Book Your Test Now</h2>
            <p className="text-xl text-gray-600">Fill out the form below and we'll contact you within 24 hours</p>
          </div>

          <Card className="border-2 border-gray-200">
            <CardHeader>
              <CardTitle className="text-2xl">Test Booking Request</CardTitle>
              <CardDescription>All fields are required</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      placeholder="John Smith"
                      value={formData.fullName}
                      onChange={(e) => handleChange('fullName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+44 7700 900000"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="testType">Test Type</Label>
                    <Select value={formData.testType} onValueChange={(value) => handleChange('testType', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select test type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="car">Car (Category B)</SelectItem>
                        <SelectItem value="motorcycle">Motorcycle</SelectItem>
                        <SelectItem value="lorry">Lorry</SelectItem>
                        <SelectItem value="bus">Bus</SelectItem>
                        <SelectItem value="adi">ADI (Driving Instructor)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">Driving License Number</Label>
                    <Input
                      id="licenseNumber"
                      placeholder="SMITH801010JD9AB"
                      value={formData.licenseNumber}
                      onChange={(e) => handleChange('licenseNumber', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferredDate">Preferred Date</Label>
                    <Input
                      id="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={(e) => handleChange('preferredDate', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="testCentre">Preferred Test Centre</Label>
                  <Input
                    id="testCentre"
                    placeholder="e.g., London (Wood Green), Manchester, Birmingham"
                    value={formData.testCentre}
                    onChange={(e) => handleChange('testCentre', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Additional Information (Optional)</Label>
                  <textarea
                    id="message"
                    className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any special requirements or preferred times?"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                  />
                </div>

                <Button type="submit" size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg">
                  Submit Booking Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Why Choose TESTBOOK LTD?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">10,000+</div>
              <div className="text-gray-600">Tests Booked</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">24/7</div>
              <div className="text-gray-600">Online Support</div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="text-4xl font-bold text-blue-900 mb-2">100%</div>
              <div className="text-gray-600">Secure Payment</div>
            </div>
          </div>

          <div className="mt-12 bg-white p-8 rounded-lg shadow-sm border-2 border-blue-200">
            <div className="flex items-start gap-4">
              <Shield className="w-12 h-12 text-blue-900 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Registered Company</h3>
                <p className="text-gray-600 mb-4">
                  TESTBOOK LTD is a registered company in England and Wales.
                  <br />
                  Company Number: <span className="font-semibold">16836503</span>
                </p>
                <p className="text-sm text-gray-500">
                  Registered Office: 20 Wenlock Road, London, England, N1 7GU
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Latest Blog Articles</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert tips and guides to help you pass your driving test
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {[
              {
                title: 'How to Prepare for Your UK Driving Test in 2025',
                excerpt: 'Essential tips and advice for passing your practical driving test on the first attempt.',
                link: '/blog/how-to-prepare-for-your-driving-test',
                category: 'Test Preparation'
              },
              {
                title: 'Top 10 Common Driving Test Mistakes to Avoid',
                excerpt: 'Learn about the most frequent errors that lead to test failures.',
                link: '/blog/common-driving-test-mistakes-to-avoid',
                category: 'Test Preparation'
              },
              {
                title: 'UK Driving Test Changes in 2025',
                excerpt: 'Stay updated with the latest changes to the UK driving test format.',
                link: '/blog/understanding-uk-driving-test-changes-2025',
                category: 'News & Updates'
              }
            ].map((post, index) => (
              <Link key={index} to={post.link} className="group">
                <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow h-full">
                  <div className="inline-block bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    {post.category}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 group-hover:text-blue-900 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <span className="text-blue-900 font-medium group-hover:underline">
                    Read More →
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/blog">
              <Button size="lg" className="bg-blue-900 hover:bg-blue-800 text-white px-8">
                View All Articles
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* DVSA Disclaimer - Moved to bottom */}
      <DVSADisclaimer />
    </div>
  );
};

export default Home;